import { jsx } from "panel";
import { selx } from "clsx";
import { UserIconEl } from "common/widgets/user-icon";
import { Placement } from "common/components/tooltip/types";
import { TrackingEventProperty as UserIconTrackingEventProperty } from "common/widgets/user-icon/tracking";
import { TrackedUserIconEl } from "common/widgets/tracked-user-icon";
import { IconSizeType } from "common/constants";
import { TooltipEl } from "common/components/tooltip";
import { getEntityCreatorDisplayName } from "common/report/stores/user-details";
import { getLastViewedStr } from "./util";
import { isTrackedUserItem } from "./types";
import { TrackingEventProperty } from "./tracking";
import styles from "./index.module.styl";
function getIconEl(user, extraAttrs) {
  var _a;
  if (isTrackedUserItem(user)) {
    return /* @__PURE__ */ jsx(TrackedUserIconEl, { attrs: {
      "distinct-id": user[`distinct-id`],
      email: user[`email`],
      avatar: user[`avatar`],
      "tooltip-text": user[`tooltip-text`],
      ...extraAttrs
    }, sel: selx(styles.userIcon) });
  }
  return /* @__PURE__ */ jsx(UserIconEl, { attrs: {
    "img-src": user[`img-src`],
    id: user[`id`],
    email: user[`email`],
    "label-prefix": user[`label-prefix`] || ``,
    "label-suffix": user[`label-suffix`] || ``,
    "is-offline": user.isOffline,
    ...extraAttrs,
    name: (_a = user.name) != null ? _a : ``
    // guard against passing 'null' or 'undefined' strings
  }, style: user.color ? {
    "--border-color": user.color,
    "--border-size": `1px`
  } : void 0, sel: selx(styles.userIcon), "data-sentry-element": "UserIconEl", "data-sentry-component": "getIconEl", "data-sentry-source-file": "template.tsx" });
}
function OverflowTooltip({
  users,
  extraUsersCount,
  moreText,
  editorUser,
  trackingProps,
  onShowed
}) {
  if (!users.length) {
    return null;
  }
  const hasLastViewed = users.some((user) => !isTrackedUserItem(user) && !!user.lastViewedUtcSeconds);
  const icons = users.map((user, index) => {
    const commonAttrs = {
      "disable-tooltip": true,
      size: IconSizeType.Small,
      "include-label": true,
      "is-offline": false,
      "track-properties": {
        [UserIconTrackingEventProperty.IconType]: index === 0 ? `creator` : `viewer`,
        [UserIconTrackingEventProperty.AccessPoint]: `views-overflow`,
        ...trackingProps ? {
          [UserIconTrackingEventProperty.Source]: trackingProps[TrackingEventProperty.Source],
          [UserIconTrackingEventProperty.ResourceType]: trackingProps[TrackingEventProperty.ResourceType]
        } : {}
      }
    };
    const rowNodes = [getIconEl(user, commonAttrs)];
    if (hasLastViewed && !isTrackedUserItem(user)) {
      let lastViewedStr = user.lastViewedUtcSeconds ? getLastViewedStr(user.lastViewedUtcSeconds) : ``;
      if (user.isOffline === false) {
        lastViewedStr = `now`;
      }
      rowNodes.push(/* @__PURE__ */ jsx("div", { sel: selx(styles.secondaryText) }, lastViewedStr));
    }
    return /* @__PURE__ */ jsx("div", { sel: selx(styles.overflowEntry) }, rowNodes);
  });
  const editorUserName = editorUser && getEntityCreatorDisplayName(editorUser.email, editorUser.name);
  return /* @__PURE__ */ jsx(TooltipEl, { attrs: {
    placement: Placement.Bottom
  }, on: {
    showed: onShowed
  }, "data-sentry-element": "TooltipEl", "data-sentry-component": "OverflowTooltip", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx("div", null, hasLastViewed && /* @__PURE__ */ jsx("div", { sel: selx(styles.overflowHeader, styles.overflowText) }, "Recently Viewed By:"), /* @__PURE__ */ jsx("div", { sel: selx(styles.overflowIcons), class: {
    "has-columns": hasLastViewed
  } }, icons), extraUsersCount ? /* @__PURE__ */ jsx("div", { sel: selx(styles.overflowMoreText, styles.overflowText) }, "+ ", extraUsersCount, " ", moreText) : null, editorUser && /* @__PURE__ */ jsx("div", { sel: selx(styles.overflowEditorSection) }, /* @__PURE__ */ jsx("div", { sel: selx(styles.overflowText) }, "Last edited ", editorUserName ? `by ${editorUserName}
` : ``, editorUser.dateStr))));
}
export function Template() {
  var _a;
  const maxIcons = this.attr(`max-icons-displayed`) >= 0 ? this.attr(`max-icons-displayed`) : 0;
  const allUsers = this.attr(`users`) || [];
  const usersToShow = allUsers.slice(0, maxIcons).reverse();
  const overflowUsers = allUsers.slice(maxIcons, this.attr(`max-icons-total`));
  const extraUsersCount = (_a = this.attr(`user-count-override`)) != null ? _a : Math.max(allUsers.length - usersToShow.length, 0);
  const extraOverflowUsersCount = Math.max(extraUsersCount - overflowUsers.length, 0);
  const icons = usersToShow.map((user, index) => {
    return getIconEl(user, {
      "disable-tooltip": this.attr(`disable-tooltip`),
      "tooltip-placement": this.attr(`tooltip-placement`),
      size: this.attr(`size`),
      "track-properties": {
        [UserIconTrackingEventProperty.IconType]: index === 0 ? `creator` : `viewer`,
        [UserIconTrackingEventProperty.AccessPoint]: `creator-icon`,
        [UserIconTrackingEventProperty.ResourceType]: this.attr(`track-properties`)[TrackingEventProperty.ResourceType]
      }
    });
  });
  const moreText = extraUsersCount ? /* @__PURE__ */ jsx("div", { sel: selx(styles.moreText) }, /* @__PURE__ */ jsx("span", null, "+ ", extraUsersCount), /* @__PURE__ */ jsx(OverflowTooltip, { users: overflowUsers, extraUsersCount: extraOverflowUsersCount, moreText: this.attr(`more-text`), editorUser: this.attr(`editor-user`), trackingProps: this.attr(`track-properties`), onShowed: this.helpers.handleOverflowTooltipShowed })) : null;
  return /* @__PURE__ */ jsx("div", { sel: selx(styles.usersList), "data-sentry-component": "Template", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx("div", { sel: selx(styles.iconContainer) }, icons), moreText);
}
